/**
 * JobsiteHomepageWorkflowsProvider - Provides workflow data for JHP via GraphQL
 *
 * This component uses the useWorkflows hook to fetch workflow data directly
 * from GraphQL, bypassing the MobX store which may not be populated on desktop.
 *
 * It reports upwards rather than wrapping its consumer in a render prop, which would build the
 * board snapshot in this component's render — outside the container's tracking scope (STX1-1009).
 */

import * as React from 'react'

import { ISitePermitFilterInput } from '~/client/graph'
import Workflow from '~/client/src/shared/models/Workflow'

import useWorkflows from '../../hooks/useWorkflows'

/** JHP time window (7 days to match backend JHP service) */
const JHP_WORKFLOW_TIME_WINDOW_DAYS = 7

interface IProps {
  projectId: string
  userId: string
  onChange: (workflows: Workflow[], loading: boolean) => void
}

const JobsiteHomepageWorkflowsProvider: React.FC<IProps> = ({
  projectId,
  userId,
  onChange,
}) => {
  // Calculate date range for workflows (JHP uses 7 days to match backend service)
  const dateRange = React.useMemo(() => {
    const now = new Date()
    const startDate = new Date(now)
    startDate.setDate(startDate.getDate() - JHP_WORKFLOW_TIME_WINDOW_DAYS)
    const endDate = new Date(now)
    endDate.setDate(endDate.getDate() + JHP_WORKFLOW_TIME_WINDOW_DAYS)
    return { startDate, endDate }
  }, [])

  // Build filter for all workflows in the time window
  const filters: ISitePermitFilterInput = React.useMemo(
    () => ({
      startDate: dateRange.startDate.getTime(),
      endDate: dateRange.endDate.getTime(),
    }),
    [dateRange],
  )

  const { workflows, workflowsLoading } = useWorkflows(
    projectId,
    userId,
    filters,
  )

  // `workflows` is useState-held inside the hook, so this fires on a real change, not per render.
  React.useEffect(() => {
    onChange(workflows, workflowsLoading)
  }, [onChange, workflows, workflowsLoading])

  return null
}

export default JobsiteHomepageWorkflowsProvider
